import { showConfirm } from '@/services/dialogService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import {
  isOfflineReadOnly,
  requireOnlineModificationAccess,
} from '@/services/offlineModificationGuardService'
import { useWorkspaceStore } from '@/stores/workspaceStore'

export type PermissionAction =
  | 'share_workspace'
  | 'delete_workspace'
  | 'rename_workspace'
  | 'edit_workspace_description'
  | 'toggle_public'
  | 'set_cover_photo'
  | 'upload_gpx'
  | 'upload_photos'
  | 'delete_photo'
  | 'edit_tour'
  | 'delete_tour'
  | 'edit_track'
  | 'delete_track'
  | 'edit_waypoint'
  | 'delete_waypoint'

const OFFLINE_BLOCKED_ACTIONS = new Set<PermissionAction>([
  'share_workspace',
  'delete_workspace',
  'rename_workspace',
  'edit_workspace_description',
  'toggle_public',
  'set_cover_photo',
  'upload_gpx',
  'upload_photos',
  'delete_photo',
  'edit_tour',
  'delete_tour',
  'edit_track',
  'delete_track',
  'edit_waypoint',
  'delete_waypoint',
])

function getOwnerNamesText(): string | null {
  const workspaceStore = useWorkspaceStore()
  const owners = workspaceStore.ownerNames ?? null
  if (owners && owners.length > 0) {
    return owners.join(', ')
  }
  return null
}

function actionReadable(action: PermissionAction): string {
  switch (action) {
    case 'share_workspace':
      return 'share this workspace'
    case 'delete_workspace':
      return 'delete this workspace'
    case 'rename_workspace':
      return 'rename this workspace'
    case 'edit_workspace_description':
      return 'edit this workspace description'
    case 'toggle_public':
      return 'change public visibility'
    case 'set_cover_photo':
      return 'change the collection cover photo'
    case 'upload_gpx':
      return 'upload GPX files'
    case 'upload_photos':
      return 'upload photos'
    case 'delete_photo':
      return 'delete photos'
    case 'edit_tour':
      return 'edit tours'
    case 'delete_tour':
      return 'delete tours'
    case 'edit_track':
      return 'edit tracks'
    case 'delete_track':
      return 'delete tracks'
    case 'edit_waypoint':
      return 'edit waypoints'
    case 'delete_waypoint':
      return 'delete waypoints'
  }
}

export function hasPermission(action: PermissionAction): boolean {
  if (isOfflineReadOnly() && OFFLINE_BLOCKED_ACTIONS.has(action)) {
    return false
  }

  const workspaceStore = useWorkspaceStore()
  const isLocalUnsyncedWorkspace =
    !!workspaceStore.clientId && workspaceStore.currentWorkspace?._createEventPushed === false

  if (action === 'delete_workspace') {
    return (
      !!workspaceStore.clientId && workspaceStore.userRole === 'Owner' && !isLocalUnsyncedWorkspace
    )
  }

  if (isLocalUnsyncedWorkspace) {
    return action !== 'share_workspace'
  }

  // No active workspace (e.g. library view) → user owns their data
  if (!workspaceStore.clientId) return true

  const canEdit = workspaceStore.canEdit === true // owner or collaborator: may contribute content
  const canManage = workspaceStore.canManageWorkspace // owner-only: collection administration

  switch (action) {
    case 'rename_workspace':
    case 'edit_workspace_description':
    case 'toggle_public':
    case 'share_workspace':
    case 'set_cover_photo':
      // Collection settings and sharing are owner-only administration.
      return canManage
    case 'upload_gpx':
    case 'upload_photos':
    case 'edit_tour':
    case 'delete_tour':
    case 'edit_track':
    case 'delete_track':
    case 'edit_waypoint':
    case 'delete_waypoint':
    case 'delete_photo':
      // Owner or collaborator may attempt to add/mutate content. Edit/delete of an existing
      // item is further restricted to its creator — see requireItemEditable, which the store
      // action calls and which shows its own explainer dialog when this user isn't the creator.
      return canEdit
    default:
      return false
  }
}

export async function requirePermission(action: PermissionAction): Promise<boolean> {
  if (OFFLINE_BLOCKED_ACTIONS.has(action)) {
    const allowed = await requireOnlineModificationAccess(action)
    if (!allowed) return false
  }

  if (hasPermission(action)) return true

  const readable = actionReadable(action)
  const ownersText = getOwnerNamesText()

  const hasMultipleOwners = ownersText ? ownersText.includes(',') : false
  const ownerWord = hasMultipleOwners ? 'owners' : 'owner'
  const message = ownersText
    ? `You don't have permission to ${readable}.

Please contact the workspace ${ownerWord} (${ownersText}) to request access.`
    : `You don't have permission to ${readable}.

Please contact the workspace owner to request access.`

  logService.warn(LOG_CATEGORIES.UI, 'Permission denied', { action, ownersText })

  await showConfirm({
    title: 'Insufficient Permissions',
    message,
    confirmText: 'OK',
    variant: 'warning',
  })
  return false
}
